// «Финансы» (этап 6, PLAN §7, DESIGN §6): P&L за период в долларах или сумах по курсу ЦБ, расходы по видам,
// выплаты сети в холде и доступные, разрез по офферам и по дням, ежедневные сверки с Meta и buyo.
// Период — общий выбор (по умолчанию «этот месяц»); длинный период сводится по неделям или месяцам.
// Таблицы сортируются по колонке — шапка таблиц видна и на телефоне.
import {
  Button,
  Card,
  Chip,
  DayChart,
  EmptyState,
  int,
  pct,
  Segmented,
  Skeleton,
  signedUsd,
  sum,
  toast,
  usd,
  when,
} from '@autoaff/ui';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Plus, Scale, Trash2, Wallet } from 'lucide-react';
import { type FormEvent, useMemo, useState } from 'react';
import { api } from '../api';
import { compareBy, PeriodPicker, periodKey, periodPhrase, SortTh, usePeriod, useSort } from '../filters';
import { PageHead } from './common';

type Money = 'usd' | 'uzs';
type Provider = 'fal' | 'gemini' | 'proxy' | 'server' | 'other';

interface Day {
  day: string;
  spend: number;
  revenue: number;
  costs: number;
  profit: number;
  leads: number;
  approved: number;
  inProcess: number;
  usdUzs: number | null;
}

interface FinanceView {
  period: string;
  since: string;
  until: string;
  days: Day[];
  totals: {
    spend: number;
    revenue: number;
    costs: number;
    profit: number;
    /** Ждём от лидов в обработке — по офферу и возрасту лида (нет у старого api). */
    expected?: number;
    forecastProfit: number;
    roi: number | null;
    leads: number;
    approved: number;
    inProcess: number;
    /** % апрува от решённых — одна формула платформы (нет у старого api). */
    approveRate?: number | null;
    uzs: { spend: number; revenue: number; costs: number; profit: number } | null;
  };
  costsByProvider: Record<Provider, number>;
  payouts: { hold: number; released: number };
  offers: Array<{
    key: string | null;
    name: string | null;
    spend: number;
    revenue: number;
    costs: number;
    profit: number;
    leads: number;
    approved: number;
  }>;
  usdUzs: number | null;
  warnings: string[];
  reconciliations: Array<{
    kind: 'meta_spend' | 'buyo_leads';
    last: {
      ok: boolean;
      periodFrom: string;
      periodTo: string;
      ours: Record<string, unknown> | null;
      theirs: Record<string, unknown> | null;
      details: Record<string, unknown> | null;
      checkedAt: string;
    } | null;
    history: Array<{ ok: boolean; checkedAt: string }>;
  }>;
  manualCosts: Array<{ id: number; day: string; provider: Provider; usd: number; note: string | null }>;
  /** По дням, как в кабинете buyo: сутки по Гринвичу, только дошедшие до сети лиды (нет у старого api). */
  networkDays?: NetworkDay[];
}

interface NetworkDay {
  day: string;
  leads: number;
  inProcess: number;
  approved: number;
  rejected: number;
  trash: number;
  payout: number;
}

const PROVIDER_RU: Record<Provider | 'ads', string> = {
  ads: 'Реклама Meta',
  fal: 'Генерации картинок и видео',
  gemini: 'Озвучка, оценка роликов, описания',
  proxy: 'Прокси для футажа',
  server: 'Сервер',
  other: 'Прочее',
};

const RECON_RU = {
  meta_spend: { title: 'Расход с Meta', text: 'объявления против итога кабинета, 7 дней' },
  buyo_leads: { title: 'Лиды с buyo', text: 'статусы и выплаты, 30 дней' },
} as const;

const dayRu = (d: string) =>
  new Date(`${d}T12:00:00Z`).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });

// ─── длинный период: по неделям или месяцам ─────────────────────────────
type Grain = 'day' | 'week' | 'month';
/** До 35 дней — по дням, до полугода — по неделям, дольше — по месяцам: подписи и столбцы не слипаются. */
const grainOf = (days: number): Grain => (days > 186 ? 'month' : days > 35 ? 'week' : 'day');
const GRAIN_RU: Record<Grain, string> = { day: 'По дням', week: 'По неделям', month: 'По месяцам' };
const MONTHS = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
const MONTHS_FULL = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
];

type Sums = Record<'spend' | 'revenue' | 'costs' | 'profit', number>;
interface Row extends Sums {
  /** День, понедельник недели или первое число месяца. */
  key: string;
  /** Первый и последний день периода в строке (неделя на краю периода бывает неполной). */
  first: string;
  last: string;
  leads: number;
  approved: number;
  /** Суммы в сумах — каждый день по своему курсу; null — курса нет хотя бы на один день. */
  uzs: Sums | null;
  /** Курс ЦБ последнего дня строки. */
  usdUzs: number | null;
}

const addDay = (d: string, n: number) =>
  new Date(Date.parse(`${d}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
const keyOf = (day: string, g: Grain) => {
  if (g === 'day') return day;
  if (g === 'month') return `${day.slice(0, 7)}-01`;
  const wd = (new Date(`${day}T00:00:00Z`).getUTCDay() + 6) % 7;
  return addDay(day, -wd);
};

function groupDays(days: Day[], g: Grain): Row[] {
  const out = new Map<string, Row>();
  for (const x of days) {
    const key = keyOf(x.day, g);
    const r = out.get(key) ?? {
      key,
      first: x.day,
      last: x.day,
      spend: 0,
      revenue: 0,
      costs: 0,
      profit: 0,
      leads: 0,
      approved: 0,
      uzs: { spend: 0, revenue: 0, costs: 0, profit: 0 },
      usdUzs: null,
    };
    for (const k of ['spend', 'revenue', 'costs', 'profit'] as const) {
      r[k] += x[k];
      if (r.uzs) {
        if (x.usdUzs) r.uzs[k] += x[k] * x.usdUzs;
        else r.uzs = null;
      }
    }
    r.leads += x.leads;
    r.approved += x.approved;
    r.last = x.day;
    r.usdUzs = x.usdUzs ?? r.usdUzs;
    out.set(key, r);
  }
  return [...out.values()];
}

/** Подпись строки: «5 сен», «1–7 сен», «Сентябрь 2026». */
function rowLabel(r: Row, g: Grain): string {
  if (g === 'day') return dayRu(r.key);
  const [y, m, d1] = r.first.split('-').map(Number) as [number, number, number];
  if (g === 'month') return `${MONTHS_FULL[m - 1]} ${y}`;
  const [, m2, d2] = r.last.split('-').map(Number) as [number, number, number];
  if (r.first === r.last) return dayRu(r.first);
  return m === m2 ? `${d1}–${d2} ${MONTHS[m - 1]}` : `${d1} ${MONTHS[m - 1]} — ${d2} ${MONTHS[m2 - 1]}`;
}
/** Подпись на оси графика: у недели — её первый день, у месяца — «сен 26». */
const axisLabel = (first: string, g: Grain) =>
  g === 'month' ? `${MONTHS[Number(first.slice(5, 7)) - 1]} ${first.slice(2, 4)}` : dayRu(first);

type OfferKey = 'name' | 'spend' | 'costs' | 'revenue' | 'profit' | 'leads' | 'approved';
type DayKey = 'day' | 'spend' | 'costs' | 'revenue' | 'profit' | 'leads';
/** Сколько строк таблицы по дням видно сразу — остальное по кнопке. */
const DAY_ROWS = 31;

/**
 * Как в кабинете buyo: те же дни (сутки buyo — по Гринвичу, с 05:00 по Ташкенту), лиды, дошедшие до сети,
 * статусы и выплата — чтобы сверить один к одному. Новые дни сверху и «Итого» внизу, как в кабинете.
 */
function NetworkDays({ days }: { days: NetworkDay[] }) {
  const [all, setAll] = useState(false);
  const rows = [...days].reverse();
  const shown = all ? rows : rows.slice(0, DAY_ROWS);
  const total = days.reduce(
    (a, x) => ({
      leads: a.leads + x.leads,
      inProcess: a.inProcess + x.inProcess,
      approved: a.approved + x.approved,
      rejected: a.rejected + x.rejected,
      trash: a.trash + x.trash,
      payout: a.payout + x.payout,
    }),
    { leads: 0, inProcess: 0, approved: 0, rejected: 0, trash: 0, payout: 0 },
  );
  return (
    <Card title="Как в кабинете buyo">
      <p className="muted fin-note">
        В кабинете buyo сутки идут по Гринвичу — с 05:00 до 05:00 по Ташкенту. Здесь те же дни и только лиды,
        дошедшие до сети, поэтому цифры совпадают с кабинетом. В графике и таблице выше дни по Ташкенту, как у
        рекламы в Meta: по дням они сдвинуты, за период почти равны.
      </p>
      <div className="fin-tbl-wrap">
        <table className="fin-tbl">
          <thead>
            <tr>
              <th>Дата</th>
              <th className="num">Лиды</th>
              <th className="num hide-m">В обработке</th>
              <th className="num">Апрувы</th>
              <th className="num hide-m">Отказы</th>
              <th className="num hide-m">Треш</th>
              <th className="num">Выплата</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((x) => (
              <tr key={x.day}>
                <td>{dayRu(x.day)}</td>
                <td className="num">{int(x.leads)}</td>
                <td className="num hide-m">{int(x.inProcess)}</td>
                <td className="num">{int(x.approved)}</td>
                <td className="num hide-m">{int(x.rejected)}</td>
                <td className="num hide-m">{int(x.trash)}</td>
                <td className="num">{usd(x.payout)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td>Итого</td>
              <td className="num">{int(total.leads)}</td>
              <td className="num hide-m">{int(total.inProcess)}</td>
              <td className="num">{int(total.approved)}</td>
              <td className="num hide-m">{int(total.rejected)}</td>
              <td className="num hide-m">{int(total.trash)}</td>
              <td className="num">{usd(total.payout)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      {rows.length > DAY_ROWS && (
        <Button size="sm" variant="ghost" block className="fin-more" onClick={() => setAll(!all)}>
          {all ? 'Свернуть' : `Показать все: ${rows.length}`}
        </Button>
      )}
    </Card>
  );
}

function AddCost({ onDone }: { onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Tashkent' });
  const [day, setDay] = useState(today);
  const [provider, setProvider] = useState<Provider>('server');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const n = Number(amount.replace(',', '.'));
    if (!Number.isFinite(n) || n <= 0) return toast('Сумма — число больше нуля');
    setBusy(true);
    try {
      await api('/api/finance/costs', {
        day,
        provider,
        usd: n,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      toast('Расход добавлен');
      setAmount('');
      setNote('');
      setOpen(false);
      onDone();
    } catch (err) {
      toast((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  if (!open)
    return (
      <Button size="sm" variant="ghost" icon={<Plus />} onClick={() => setOpen(true)}>
        Добавить расход
      </Button>
    );
  return (
    <form className="fin-add" onSubmit={(e) => void submit(e)}>
      <input
        className="input"
        type="date"
        aria-label="День"
        value={day}
        max={today}
        onChange={(e) => setDay(e.target.value)}
      />
      <select
        className="input"
        aria-label="Вид расхода"
        value={provider}
        onChange={(e) => setProvider(e.target.value as Provider)}
      >
        <option value="server">Сервер</option>
        <option value="proxy">Прокси</option>
        <option value="other">Прочее</option>
      </select>
      <input
        className="input tnum"
        inputMode="decimal"
        placeholder="$"
        aria-label="Сумма, долларов"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
      />
      <input
        className="input"
        placeholder="за что (для себя)"
        aria-label="Комментарий"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      <div className="fin-add-acts">
        <Button type="submit" size="sm" variant="primary" disabled={busy}>
          Добавить
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
          Отмена
        </Button>
      </div>
    </form>
  );
}

export function FinancePage() {
  const qc = useQueryClient();
  const [period, setPeriod] = usePeriod('finance', 'month');
  const [money, setMoney] = useState<Money>('usd');
  const [offerSort, sortOffers] = useSort<OfferKey>({ key: 'revenue', dir: 'desc' });
  const [daySort, sortDays] = useSort<DayKey>({ key: 'day', dir: 'desc' });
  const [allDays, setAllDays] = useState(false);
  const q = useQuery({
    queryKey: ['finance', periodKey(period)],
    queryFn: () => api<FinanceView>(`/api/finance?period=${periodKey(period)}`),
    placeholderData: keepPreviousData,
  });
  const d = q.data;
  const grain = grainOf(d?.days.length ?? 0);
  const rows = useMemo(() => (d ? groupDays(d.days, grain) : []), [d, grain]);
  const tableRows = useMemo(
    () =>
      [...rows].sort(compareBy<Row>((r) => (daySort.key === 'day' ? r.key : r[daySort.key]), daySort.dir)),
    [rows, daySort],
  );
  const offerRows = useMemo(
    () =>
      [...(d?.offers ?? [])].sort(
        compareBy(
          (o) => (offerSort.key === 'name' ? (o.name ?? 'Без оффера') : o[offerSort.key]),
          offerSort.dir,
        ),
      ),
    [d, offerSort],
  );
  // В сумах — только когда курс ЦБ есть на все дни
  const uzsOk = money === 'uzs' && !!d?.totals.uzs;
  const fmt = (n: number, day?: string) => {
    if (money === 'usd' || !d) return usd(n);
    const rate = day ? d.days.find((x) => x.day === day)?.usdUzs : d.usdUzs;
    return rate ? sum(Math.round(n * rate)) : usd(n);
  };
  const fmtSigned = (n: number, day?: string) =>
    money === 'usd' || !d ? signedUsd(n) : `${n > 0 ? '+' : n < 0 ? '−' : ''}${fmt(Math.abs(n), day)}`;
  /** Строка по дням/неделям/месяцам: в сумах — по курсу каждого дня. */
  const fmtRow = (r: Row, k: keyof Sums) =>
    money === 'uzs' && r.uzs ? sum(Math.round(r.uzs[k])) : usd(r[k]);
  const fmtRowSigned = (r: Row) => {
    const n = money === 'uzs' && r.uzs ? r.uzs.profit : r.profit;
    return money === 'uzs' && r.uzs
      ? `${n > 0 ? '+' : n < 0 ? '−' : ''}${sum(Math.round(Math.abs(n)))}`
      : signedUsd(n);
  };
  const tot = (k: 'spend' | 'revenue' | 'costs' | 'profit') =>
    uzsOk && d?.totals.uzs ? sum(d.totals.uzs[k]) : usd(d?.totals[k] ?? 0);
  const tone = (n: number) => (n > 0 ? 'pos' : n < 0 ? 'neg' : undefined);
  const refresh = () => void qc.invalidateQueries({ queryKey: ['finance'] });
  const removeCost = async (id: number) => {
    try {
      await api(`/api/finance/costs/${id}`, undefined, 'DELETE');
      toast('Расход удалён');
      refresh();
    } catch (e) {
      toast((e as Error).message);
    }
  };

  const empty =
    d && d.totals.spend === 0 && d.totals.revenue === 0 && d.totals.costs === 0 && d.totals.leads === 0;
  const breakdown = d
    ? (
        [
          ['ads', d.totals.spend],
          ...(Object.entries(d.costsByProvider) as Array<[Provider, number]>),
        ] as Array<[Provider | 'ads', number]>
      ).filter(([, v]) => v > 0)
    : [];
  const outTotal = breakdown.reduce((a, [, v]) => a + v, 0);
  // Сверка с кабинетом buyo — когда за период есть лиды, дошедшие до сети
  const networkShown = !!d?.networkDays?.some((x) => x.leads > 0);

  return (
    <>
      <PageHead
        title="Финансы"
        sub={
          d
            ? `${dayRu(d.since)} — ${dayRu(d.until)}${d.usdUzs ? ` · курс ЦБ ${sum(Math.round(d.usdUzs))} за $1` : ''}`
            : 'Доходы и расходы в долларах и сумах по курсу ЦБ'
        }
        actions={
          <div className="fin-controls">
            <PeriodPicker value={period} onChange={setPeriod} months />
            <Segmented
              label="Валюта"
              value={money}
              onChange={setMoney}
              options={[
                { value: 'usd', label: '$' },
                { value: 'uzs', label: 'сум' },
              ]}
            />
          </div>
        }
      />
      <div className="pc">
        {q.isPending ? (
          <>
            <div className="kpis">
              {Array.from({ length: 5 }, (_, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: скелетоны без данных
                <div key={i} className="kpi">
                  <Skeleton w="50%" />
                  <Skeleton w="70%" h={26} />
                </div>
              ))}
            </div>
            <div className="skeleton" style={{ height: 260, borderRadius: 'var(--r-lg)' }} />
          </>
        ) : q.isError || !d ? (
          <div className="card">
            <EmptyState
              icon={<Wallet />}
              title="Не удалось загрузить финансы"
              action={<Button onClick={() => q.refetch()}>Повторить</Button>}
            >
              {(q.error as Error | null)?.message}
            </EmptyState>
          </div>
        ) : (
          <>
            {money === 'uzs' && !d.totals.uzs && (
              <p className="notice">Курса ЦБ нет на часть дней периода — суммы показаны в долларах.</p>
            )}
            {empty && (
              <div className="card">
                <EmptyState
                  icon={<Wallet />}
                  title={`${periodPhrase(period).replace(/^за /, 'За ')} денег ещё нет`}
                  action={
                    <Link to="/settings" search={{ tab: 'integrations' }} className="btn">
                      Открыть интеграции
                    </Link>
                  }
                >
                  Выплаты приходят из buyo, расход — из кабинетов Meta. Когда они подключены, P&L считается
                  сам, а генерации и прокси платформа записывает в расходы без вас.
                </EmptyState>
              </div>
            )}
            <section className="kpis" aria-label={`Итоги ${periodPhrase(period)}`}>
              <div className="kpi">
                <span>Выплаты сети</span>
                <b>{tot('revenue')}</b>
                <small>
                  {int(d.totals.approved)} апрувов из {int(d.totals.leads)} лидов
                  {d.totals.approveRate !== null && d.totals.approveRate !== undefined && (
                    <>
                      <br />
                      {pct(d.totals.approveRate)} от решённых
                    </>
                  )}
                </small>
              </div>
              <div className="kpi">
                <span>Реклама Meta</span>
                <b>{tot('spend')}</b>
                <small>
                  {d.totals.leads > 0 ? `CPL ${fmt(d.totals.spend / d.totals.leads)}` : 'лидов пока нет'}
                </small>
              </div>
              <div className="kpi">
                <span>Прочие расходы</span>
                <b>{tot('costs')}</b>
                <small>генерации, прокси, сервер</small>
              </div>
              <div className="kpi">
                <span>Прибыль</span>
                <b className={tone(d.totals.profit)}>
                  {uzsOk && d.totals.uzs
                    ? `${d.totals.uzs.profit > 0 ? '+' : d.totals.uzs.profit < 0 ? '−' : ''}${sum(Math.abs(d.totals.uzs.profit))}`
                    : signedUsd(d.totals.profit)}
                </b>
                <small>
                  {d.totals.roi !== null ? `окупаемость ${d.totals.roi.toFixed(1)}%` : 'расходов нет'}
                </small>
              </div>
              <div className="kpi">
                <span>Прогноз прибыли</span>
                <b className={tone(d.totals.forecastProfit)}>{fmtSigned(d.totals.forecastProfit)}</b>
                <small>
                  {d.totals.inProcess > 0
                    ? d.totals.expected !== undefined
                      ? `+${fmt(d.totals.expected)} ждём от ${int(d.totals.inProcess)} в обработке`
                      : `ещё ${int(d.totals.inProcess)} лидов в обработке`
                    : 'все лиды с решением'}
                </small>
              </div>
            </section>

            {/* Две колонки, каждая своей высоты: под «По дням» нет пустого места до «По офферам» */}
            <div className="fin-grid">
              <div className="fin-col">
                <Card title={GRAIN_RU[grain]}>
                  <DayChart
                    points={rows.map((x) => ({ day: x.first, bar: x.spend + x.costs, line: x.revenue }))}
                    labels={{ bar: 'Расходы', line: 'Выплаты' }}
                    format={(n) => usd(n)}
                    {...(grain === 'day' ? {} : { xLabel: (k: string) => axisLabel(k, grain) })}
                    tooltip={(p) => {
                      const x = rows.find((y) => y.first === p.day);
                      if (!x) return null;
                      return (
                        <>
                          {grain !== 'day' && <span>{rowLabel(x, grain)}</span>}
                          <span>Реклама: {fmtRow(x, 'spend')}</span>
                          {x.costs > 0 && <span>Прочее: {fmtRow(x, 'costs')}</span>}
                          <span>
                            Лиды: {int(x.leads)} · апрувы: {int(x.approved)}
                          </span>
                          <span>Выплаты: {fmtRow(x, 'revenue')}</span>
                          <span className={x.profit >= 0 ? 'pos' : 'neg'}>Прибыль: {fmtRowSigned(x)}</span>
                        </>
                      );
                    }}
                  />
                  {networkShown && (
                    <p className="muted fin-note fin-note-after">
                      Дни по Ташкенту, как у рекламы в Meta. Сверить с кабинетом buyo — таблица «Как в
                      кабинете buyo» ниже.
                    </p>
                  )}
                </Card>
                <Card title="По офферам">
                  {d.offers.length === 0 && (
                    <p className="muted" style={{ margin: 0, fontSize: 'var(--fs-sm)' }}>
                      Когда пойдут лиды и расход, здесь будет прибыль по каждому офферу.
                    </p>
                  )}
                  <div className="fin-tbl-wrap" hidden={d.offers.length === 0}>
                    <table className="fin-tbl">
                      <thead>
                        <tr>
                          <SortTh k="name" sort={offerSort} onSort={sortOffers} firstDir="asc">
                            Оффер
                          </SortTh>
                          <SortTh k="spend" sort={offerSort} onSort={sortOffers} className="num">
                            Реклама
                          </SortTh>
                          <SortTh k="costs" sort={offerSort} onSort={sortOffers} className="num hide-m">
                            Прочее
                          </SortTh>
                          <SortTh k="revenue" sort={offerSort} onSort={sortOffers} className="num">
                            Выплаты
                          </SortTh>
                          <SortTh k="profit" sort={offerSort} onSort={sortOffers} className="num">
                            Прибыль
                          </SortTh>
                          <SortTh k="leads" sort={offerSort} onSort={sortOffers} className="num hide-m">
                            Лиды
                          </SortTh>
                          <SortTh k="approved" sort={offerSort} onSort={sortOffers} className="num hide-m">
                            Апрувы
                          </SortTh>
                        </tr>
                      </thead>
                      <tbody>
                        {offerRows.map((o) => (
                          <tr key={o.key ?? 'none'}>
                            <td>{o.name ?? 'Без оффера'}</td>
                            <td className="num">{fmt(o.spend)}</td>
                            <td className="num hide-m">{fmt(o.costs)}</td>
                            <td className="num">{fmt(o.revenue)}</td>
                            <td className={`num ${tone(o.profit) ?? ''}`}>{fmtSigned(o.profit)}</td>
                            <td className="num hide-m">{int(o.leads)}</td>
                            <td className="num hide-m">{int(o.approved)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </div>
              <div className="fin-col">
                <Card title="Расходы" action={<AddCost onDone={refresh} />}>
                  {breakdown.length === 0 ? (
                    <p className="muted" style={{ margin: 0, fontSize: 'var(--fs-sm)' }}>
                      Расходов за период нет.
                    </p>
                  ) : (
                    <ul className="fin-bars">
                      {breakdown.map(([k, v]) => (
                        <li key={k}>
                          <div className="fin-bar-row">
                            <span>{PROVIDER_RU[k]}</span>
                            <b className="tnum">{fmt(v)}</b>
                          </div>
                          <div className="meter" aria-hidden="true">
                            <i style={{ width: `${Math.max(2, (v / outTotal) * 100)}%` }} />
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                  {d.manualCosts.length > 0 && (
                    <>
                      <h3 className="fin-h">Добавлено вручную</h3>
                      <ul className="fin-manual">
                        {d.manualCosts.map((m) => (
                          <li key={m.id}>
                            <span className="tnum">{dayRu(m.day)}</span>
                            <span className="min-w-0">
                              {PROVIDER_RU[m.provider]}
                              {m.note ? ` · ${m.note}` : ''}
                            </span>
                            <b className="tnum">{usd(m.usd)}</b>
                            <button
                              type="button"
                              className="auto-x"
                              aria-label={`Удалить расход ${usd(m.usd)}`}
                              onClick={() => void removeCost(m.id)}
                            >
                              <Trash2 />
                            </button>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </Card>
                <Card title="Выплаты сети">
                  <div className="fin-pay">
                    <div>
                      <span>В холде</span>
                      <b className="tnum">{fmt(d.payouts.hold)}</b>
                    </div>
                    <div>
                      <span>Холд прошёл</span>
                      <b className="tnum">{fmt(d.payouts.released)}</b>
                    </div>
                  </div>
                  <p className="muted" style={{ margin: '10px 0 0', fontSize: 'var(--fs-sm)' }}>
                    За апрувы лидов этого периода. Холд buyo — 3 дня после апрува.
                  </p>
                  <h3 className="fin-h">Сверки</h3>
                  <ul className="fin-recon">
                    {d.reconciliations.map((r) => (
                      <li key={r.kind}>
                        <Scale aria-hidden="true" />
                        <div className="min-w-0">
                          <b>{RECON_RU[r.kind].title}</b>
                          <span>
                            {r.last
                              ? `${RECON_RU[r.kind].text} · ${when(r.last.checkedAt)}`
                              : 'ещё не проводилась — каждое утро, когда источник подключён'}
                          </span>
                        </div>
                        {r.last &&
                          (r.last.ok ? (
                            <Chip tone="success">Сходится</Chip>
                          ) : (
                            <Chip tone="warning">Расходится</Chip>
                          ))}
                      </li>
                    ))}
                  </ul>
                </Card>
              </div>
            </div>

            <Card title={`${GRAIN_RU[grain]} — таблица`}>
              <div className="fin-tbl-wrap">
                <table className="fin-tbl">
                  <thead>
                    <tr>
                      <SortTh k="day" sort={daySort} onSort={sortDays}>
                        {grain === 'day' ? 'День' : grain === 'week' ? 'Неделя' : 'Месяц'}
                      </SortTh>
                      <SortTh k="spend" sort={daySort} onSort={sortDays} className="num">
                        Реклама
                      </SortTh>
                      <SortTh k="costs" sort={daySort} onSort={sortDays} className="num hide-m">
                        Прочее
                      </SortTh>
                      <SortTh k="revenue" sort={daySort} onSort={sortDays} className="num">
                        Выплаты
                      </SortTh>
                      <SortTh k="profit" sort={daySort} onSort={sortDays} className="num">
                        Прибыль
                      </SortTh>
                      <SortTh k="leads" sort={daySort} onSort={sortDays} className="num hide-m">
                        Лиды
                      </SortTh>
                      <th className="num hide-m">Курс ЦБ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(allDays ? tableRows : tableRows.slice(0, DAY_ROWS)).map((x) => (
                      <tr key={x.key}>
                        <td>{rowLabel(x, grain)}</td>
                        <td className="num">{fmtRow(x, 'spend')}</td>
                        <td className="num hide-m">{fmtRow(x, 'costs')}</td>
                        <td className="num">{fmtRow(x, 'revenue')}</td>
                        <td className={`num ${tone(x.profit) ?? ''}`}>{fmtRowSigned(x)}</td>
                        <td className="num hide-m">{int(x.leads)}</td>
                        <td className="num hide-m">{x.usdUzs ? sum(Math.round(x.usdUzs)) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {tableRows.length > DAY_ROWS && (
                <Button
                  size="sm"
                  variant="ghost"
                  block
                  className="fin-more"
                  onClick={() => setAllDays(!allDays)}
                >
                  {allDays ? 'Свернуть' : `Показать все: ${tableRows.length}`}
                </Button>
              )}
            </Card>

            {networkShown && d.networkDays && <NetworkDays days={d.networkDays} />}

            {d.warnings.length > 0 && (
              <ul className="fin-warn">
                {d.warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </>
  );
}
